import { formatRate } from '@polaris/core/utils'
import type { ComponentProps } from 'react'
import { cn } from '@/src/utils/styles'
import { Triangle } from './icons/triangle'
import { Skeleton } from './skeleton'

interface PriceChangeProps {
  change: number
  size?: 'sm' | 'md'
  hideIfZero?: boolean
  rightText?: string
  isLoading?: boolean
}

export const PriceChange = (
  props: ComponentProps<'div'> & PriceChangeProps,
) => {
  const {
    change,
    className,
    size = 'sm',
    hideIfZero = true,
    rightText,
    isLoading = false,
    ...rest
  } = props

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-1', className)} {...rest}>
        <Skeleton
          className={cn('rounded', {
            'w-4 h-4': size === 'sm',
            'w-3 h-5': size === 'md',
          })}
        />
        <Skeleton
          className={cn('rounded', {
            'w-12 h-4': size === 'sm',
            'w-10 h-5': size === 'md',
          })}
        />
        {rightText && (
          <Skeleton
            className={cn('rounded', {
              'w-8 h-4': size === 'sm',
              'w-10 h-5': size === 'md',
            })}
          />
        )}
      </div>
    )
  }

  const percentageText = formatRate(change / 100, {
    maximumFractionDigits: 2,
    forcePositiveSign: true,
  })

  const hidden =
    !change || change.toString().includes('e') || percentageText === '0%'

  if (hidden && hideIfZero) {
    return null
  }

  const isChangeNegative = change < 0

  return (
    <div
      className={cn('flex items-center text-success-text', className, {
        'text-danger-text': isChangeNegative,
        'text-tertiary': hidden,
      })}
      {...rest}
    >
      {!hidden ? (
        <Triangle
          width={size === 'sm' ? 16 : 24}
          height={size === 'sm' ? 16 : 24}
          className={cn('size-4', {
            'rotate-180': isChangeNegative,
            'size-6': size === 'md',
          })}
        />
      ) : null}
      <span
        className={cn('text-sm', {
          'text-base': size === 'md',
        })}
      >
        {percentageText}
      </span>
      {rightText && (
        <span
          className={cn('text-sm ml-1', {
            'text-base': size === 'md',
          })}
        >
          {rightText}
        </span>
      )}
    </div>
  )
}
